// @flow

import { getJSON } from 'redux-api-middleware';
import { I18n } from '@shopify/react-i18n';
import { processRes } from 'src/helpers/actions-helper';

import apiService from 'src/services/api-service';
import {
  GET_KLAVIYO_STATUS_REQUEST,
  GET_KLAVIYO_STATUS_SUCCESS,
  GET_KLAVIYO_STATUS_FAILURE,
  CHECK_KLAVIYO_INSTALL_STATUS_REQUEST,
  CHECK_KLAVIYO_INSTALL_STATUS_SUCCESS,
  CHECK_KLAVIYO_INSTALL_STATUS_FAILURE,
  GENERATE_KLAVIYO_LINK_REQUEST,
  GENERATE_KLAVIYO_LINK_SUCCESS,
  GENERATE_KLAVIYO_LINK_FAILURE,
  OAUTH_KLAVIYO_REQUEST,
  OAUTH_KLAVIYO_SUCCESS,
  OAUTH_KLAVIYO_FAILURE,
  TOGGLE_KLAVIYO_REQUEST,
  TOGGLE_KLAVIYO_SUCCESS,
  TOGGLE_KLAVIYO_FAILURE,
  UPDATE_KLAVIYO_BANNER_REQUEST,
  UPDATE_KLAVIYO_BANNER_SUCCESS,
  UPDATE_KLAVIYO_BANNER_FAILURE,
  PREVIEW_KLAVIYO_RECOMMENDATION_REQUEST,
  PREVIEW_KLAVIYO_RECOMMENDATION_SUCCESS,
  PREVIEW_KLAVIYO_RECOMMENDATION_FAILURE,
  GET_KLAVIYO_RECOMMENDATIONS_REQUEST,
  GET_KLAVIYO_RECOMMENDATIONS_SUCCESS,
  GET_KLAVIYO_RECOMMENDATIONS_FAILURE,
  GET_KLAVIYO_RECOMMENDATION_REQUEST,
  GET_KLAVIYO_RECOMMENDATION_SUCCESS,
  GET_KLAVIYO_RECOMMENDATION_FAILURE,
  CREATE_KLAVIYO_RECOMMENDATION_REQUEST,
  CREATE_KLAVIYO_RECOMMENDATION_SUCCESS,
  CREATE_KLAVIYO_RECOMMENDATION_FAILURE,
  UPDATE_KLAVIYO_RECOMMENDATION_REQUEST,
  UPDATE_KLAVIYO_RECOMMENDATION_SUCCESS,
  UPDATE_KLAVIYO_RECOMMENDATION_FAILURE,
  DELETE_KLAVIYO_RECOMMENDATION_REQUEST,
  DELETE_KLAVIYO_RECOMMENDATION_SUCCESS,
  DELETE_KLAVIYO_RECOMMENDATION_FAILURE
} from 'stores/flow-type/klaviyo.type';

/**
 * call to get klaviyo status
 * @return {object} The RSAA object for API middleware
 */
export const getKlaviyoStatus = (i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.get-klaviyo-integration-status',
    {},
    [
      GET_KLAVIYO_STATUS_REQUEST,
      {
        type: GET_KLAVIYO_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);
          return { data };
        }
      },
      {
        type: GET_KLAVIYO_STATUS_FAILURE,
        payload: async (action, state, res) => {
          const errorResult = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.getKlaviyoStatus_Failure'),
            i18n
          );

          return errorResult;
        }
      }
    ]
  );
};

/**
 * Check whether Boost is installed on a shop, via the backend's unauthenticated
 * `admin/klaviyo/oauth/install` endpoint (see docs/klaviyo-embedded-install-login-flow.md §9).
 * Called from the Klaviyo-embedded-install domain-input form (`klaviyo/install.jsx`) on submit,
 * before any Shopify redirect.
 * @return {object} The RSAA object for API middleware
 */
export const checkKlaviyoInstallStatus = (shop: string, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.check-klaviyo-install-status',
    { queries: { shop } },
    [
      CHECK_KLAVIYO_INSTALL_STATUS_REQUEST,
      {
        type: CHECK_KLAVIYO_INSTALL_STATUS_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);

          return { data };
        }
      },
      {
        type: CHECK_KLAVIYO_INSTALL_STATUS_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to generate klaviyo link oauth
 * @return {object} The RSAA object for API middleware
 */
export const generateKlaviyoLink = (redirectURI: string, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.generate-klaviyo-link',
    { body: { redirectURI } },
    [
      GENERATE_KLAVIYO_LINK_REQUEST,
      {
        type: GENERATE_KLAVIYO_LINK_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: GENERATE_KLAVIYO_LINK_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to callback klaviyo oauth
 * @return {object} The RSAA object for API middleware
 */
export const callbackOauthKlaviyo = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('third-party-integration.klaviyo.auth-klaviyo', { body }, [
    OAUTH_KLAVIYO_REQUEST,
    {
      type: OAUTH_KLAVIYO_SUCCESS,
      payload: async (action, state, res) => {
        const data = await getJSON(res);

        return { data };
      }
    },
    {
      type: OAUTH_KLAVIYO_FAILURE,
      payload: async (action, state, res) => {
        const error = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.KlaviyoIntegration.Actions.oauthKlaviyo_Failure'),
          i18n
        );
        return error;
      }
    }
  ]);
};

/**
 * call to toggle klaviyo status
 * @return {object} The RSAA object for API middleware
 */
export const toggleKlaviyo = (body: Object, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.toggle-klaviyo-integration',
    { body },
    [
      {
        type: TOGGLE_KLAVIYO_REQUEST,
        payload: body.status
      },
      {
        type: TOGGLE_KLAVIYO_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: TOGGLE_KLAVIYO_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.toggleKlaviyo_Failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * call to toggle klaviyo status
 * @return {object} The RSAA object for API middleware
 */
export const updateKlaviyoBanner = (body: Object) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.update-klaviyo-integration-banner',
    { body },
    [
      {
        type: UPDATE_KLAVIYO_BANNER_REQUEST,
        payload: body
      },
      {
        type: UPDATE_KLAVIYO_BANNER_SUCCESS,
        payload: async (action, state, res) => {
          const data = await getJSON(res);

          return { data };
        }
      },
      {
        type: UPDATE_KLAVIYO_BANNER_FAILURE,
        payload: async (action, state, res) => {
          const error = await getJSON(res);
          return { data: body, error };
        }
      }
    ]
  );
};

/**
 * Preview real products matching a Klaviyo recommendation's type + filtering rules — same
 * "live preview against real store data" pattern as merchandising-v2's previewMerchandisingV2Rule.
 * Body: { recommendationType, rules }.
 * @return {object} The RSAA object for API middleware
 */
export const previewKlaviyoRecommendation = (body: Object, i18n: I18n) => {
  return apiService.sendRequest('third-party-integration.klaviyo.preview-recommendation', { body }, [
    PREVIEW_KLAVIYO_RECOMMENDATION_REQUEST,
    {
      type: PREVIEW_KLAVIYO_RECOMMENDATION_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);
        return { data };
      }
    },
    {
      type: PREVIEW_KLAVIYO_RECOMMENDATION_FAILURE,
      payload: async (action, state, res) => {
        const error = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
          i18n
        );
        return error;
      }
    }
  ]);
};

/**
 * List a shop's Klaviyo recommendations (BRD Screen 1 "Your recommendations" list).
 * @return {object} The RSAA object for API middleware
 */
export const getKlaviyoRecommendations = (i18n: I18n) => {
  return apiService.sendRequest('third-party-integration.klaviyo.get-recommendations', {}, [
    GET_KLAVIYO_RECOMMENDATIONS_REQUEST,
    {
      type: GET_KLAVIYO_RECOMMENDATIONS_SUCCESS,
      payload: async (action, state, res) => {
        const { data } = await getJSON(res);
        return { data };
      }
    },
    {
      type: GET_KLAVIYO_RECOMMENDATIONS_FAILURE,
      payload: async (action, state, res) => {
        const error = await processRes(
          res,
          'error',
          i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
          i18n
        );
        return error;
      }
    }
  ]);
};

/**
 * Get one Klaviyo recommendation by id (used by the Edit / "Get code" flows).
 * @return {object} The RSAA object for API middleware
 */
export const getKlaviyoRecommendation = (recommendationId: string, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.get-recommendation',
    { params: { recommendationId } },
    [
      GET_KLAVIYO_RECOMMENDATION_REQUEST,
      {
        type: GET_KLAVIYO_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);
          return { data };
        }
      },
      {
        type: GET_KLAVIYO_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * Create a Klaviyo recommendation. Body: { name, recommendationType, rules, widgetUI }.
 * BRD 2.6: the backend rejects with a 400 ("No products to display...") when 0 products match —
 * that error surfaces via the FAILURE branch below like any other API error.
 * @return {object} The RSAA object for API middleware
 */
export const createKlaviyoRecommendation = (body: Object, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.create-recommendation',
    { body },
    [
      CREATE_KLAVIYO_RECOMMENDATION_REQUEST,
      {
        type: CREATE_KLAVIYO_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);
          return { data };
        }
      },
      {
        type: CREATE_KLAVIYO_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * Update a Klaviyo recommendation's name/rules/widgetUI. Body must NOT include
 * recommendationType — BRD Screen 1 #6: rec type is locked once created, and the backend ignores
 * it from the body regardless.
 * @return {object} The RSAA object for API middleware
 */
export const updateKlaviyoRecommendation = (
  recommendationId: string,
  body: Object,
  i18n: I18n
) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.update-recommendation',
    { params: { recommendationId }, body },
    [
      UPDATE_KLAVIYO_RECOMMENDATION_REQUEST,
      {
        type: UPDATE_KLAVIYO_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);
          return { data };
        }
      },
      {
        type: UPDATE_KLAVIYO_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};

/**
 * Delete a Klaviyo recommendation (BRD 1.7: also stops its web feed from serving data).
 * @return {object} The RSAA object for API middleware
 */
export const deleteKlaviyoRecommendation = (recommendationId: string, i18n: I18n) => {
  return apiService.sendRequest(
    'third-party-integration.klaviyo.delete-recommendation',
    { params: { recommendationId } },
    [
      { type: DELETE_KLAVIYO_RECOMMENDATION_REQUEST, payload: { recommendationId } },
      {
        type: DELETE_KLAVIYO_RECOMMENDATION_SUCCESS,
        payload: async (action, state, res) => {
          const { data } = await getJSON(res);
          return { data, recommendationId };
        }
      },
      {
        type: DELETE_KLAVIYO_RECOMMENDATION_FAILURE,
        payload: async (action, state, res) => {
          const error = await processRes(
            res,
            'error',
            i18n.translate('AdminHTML.KlaviyoIntegration.Actions.failure'),
            i18n
          );
          return error;
        }
      }
    ]
  );
};
